import { useBatches } from '~/features/catalog-operations/hooks/use-catalog-batches';
import type { FetchBatchesParams } from '~/features/catalog-operations/services/catalog-operations.service';

/** Origin tag that isolates Feed Audit runs from the shared enrichment-jobs list. */
export const FEED_AUDIT_SOURCE = 'FEED_AUDIT';

/**
 * Loads past Feed Audit runs — the shared `/v1/enrichments/jobs` listing scoped
 * to `source=FEED_AUDIT`. Returns the standard mapped BatchListPage.
 */
export function useFeedAuditList() {
  return useBatches({ source: FEED_AUDIT_SOURCE, page: 0, size: 50 });
}

/** Existence probe: one row is enough, and `archived: 'ALL'` is deliberate —
 *  the default list hides archived rows, and an archived audit must still count
 *  towards the one-audit-per-subsidiary limit, or archiving it would unlock a
 *  second run. */
const FEED_AUDIT_BATCH_PROBE_PARAMS: FetchBatchesParams = {
  source: FEED_AUDIT_SOURCE,
  page: 0,
  size: 1,
  archived: 'ALL',
};

/** Whether the subsidiary already has any Feed Audit batch (any status, archived
 *  or not), plus the probe's loading state — single source of truth for "an audit
 *  already exists", used to block creating a second audit across entry points. */
export function useHasFeedAuditBatch(): { hasBatch: boolean; isLoading: boolean } {
  const { data, isLoading } = useBatches(FEED_AUDIT_BATCH_PROBE_PARAMS);
  return {
    hasBatch: (data?.total ?? 0) > 0,
    isLoading,
  };
}
